/**
 * Таксономия ролей категорий (ADR 0008): роли — данные, не код.
 * Базовые роли (здесь) ⊕ venue.taxonomy (данные площадки, spread — venue
 * выигрывает). Убирает хардкоды 'parking'×5, 'checkpoint'-раскраску и закрытый
 * whitelist инфраструктуры. Поля — snake_case, совпадают с JSON RVF 1:1
 * (venue.taxonomy спредится без маппинга).
 */
import type { CategorySpec, Venue } from './types'

/** Базовые роли категорий. venue.taxonomy их переопределяет точечно. */
const BASE: Readonly<Record<string, CategorySpec>> = {
  parking: { role: 'parking', height_px: 6 },
  service: { role: 'service' },
  checkpoint: { role: 'destination', accent: true },
  entrance: { role: 'infra', infra_kind: 'entrance', size_m: [12, 1.6] },
  wc: { role: 'infra', infra_kind: 'wc', size_m: [4.8, 5.6] },
  info: { role: 'infra', infra_kind: 'info', size_m: [4, 4] },
  escalator: { role: 'connector', infra_kind: 'escalator', size_m: [4.8, 12] },
  stairs: { role: 'connector', infra_kind: 'stairs', size_m: [4.8, 12] },
  elevator: { role: 'connector', infra_kind: 'elevator', size_m: [5.6, 5.6] },
  // Точка сканирования QR (щит/наклейка): акцент на всех картах.
  qr: { role: 'infra', infra_kind: 'sign', accent: true, size_m: [0.8, 4.8] },
  bench: { role: 'infra', infra_kind: 'bench', size_m: [8, 2] },
  // Банкомат и зона отдыха — удобства площадки, а не арендаторы: в счётчике
  // «сколько тут магазинов» им не место (фидбек владельца 28.07).
  atm: { role: 'infra', infra_kind: 'atm', size_m: [1.2, 1.2] },
  rest_area: { role: 'infra', infra_kind: 'bench', size_m: [6, 6] },
  plant: { role: 'infra', infra_kind: 'plant', size_m: [3.2, 3.2] },
  sign: { role: 'infra', infra_kind: 'sign', size_m: [0.8, 4.8] },
}

/** Спека категории: BASE ⊕ venue.taxonomy (данные площадки выигрывают). */
export function specOf(venue: Venue, category: string): CategorySpec {
  return { ...BASE[category], ...venue.taxonomy?.[category] }
}

/** Единая точка правды «это парковочная зона» (вместо 5 хардкодов 'parking'). */
export function isParking(venue: Venue, category: string): boolean {
  return specOf(venue, category).role === 'parking'
}

/**
 * Плоская ли парковка: без крыши и без нескольких этажей она рисуется просто
 * площадкой, а не коробкой. Правило одно на весь продукт — редактор, карта
 * вьюера и объёмные виды обязаны решать одинаково (фидбек владельца 29.07).
 *
 * Крытая парковка или паркинг в несколько этажей — это здание, оно поднимается.
 */
export function isFlatParking(
  venue: Venue,
  unit: { category: string; attrs?: Readonly<Record<string, unknown>> | undefined },
): boolean {
  if (!isParking(venue, unit.category)) return false
  if (unit.attrs?.['covered'] === true) return false
  const floors = Number(unit.attrs?.['floors'] ?? 0)
  return !(Number.isFinite(floors) && floors > 1)
}

/** Для кого парковка: грузовая (фуры) или обычная. */
export function parkingFor(unit: {
  attrs?: Readonly<Record<string, unknown>> | undefined
}): 'truck' | 'car' {
  return unit.attrs?.['parking_for'] === 'truck' ? 'truck' : 'car'
}

/** Служебная зона (парковка/техпомещение) — низкая призма в «Объёме». */
export function isService(venue: Venue, category: string): boolean {
  const role = specOf(venue, category).role
  return role === 'service' || role === 'parking'
}

/** Категория рендерится как объект инфраструктуры (в «Объёме»). */
export function infraKindOf(venue: Venue, category: string): string | null {
  const spec = specOf(venue, category)
  return spec.role === 'infra' || spec.role === 'connector' ? (spec.infra_kind ?? null) : null
}

/** Категории, красящиеся акцентом на карте (для data-driven слоя POI). */
export function accentCategories(venue: Venue): string[] {
  const cats = new Set<string>()
  for (const [cat, spec] of Object.entries(BASE)) if (spec.accent) cats.add(cat)
  for (const [cat, spec] of Object.entries(venue.taxonomy ?? {})) {
    if (spec.accent) cats.add(cat)
    else if (spec.accent === false) cats.delete(cat)
  }
  return [...cats]
}
